import { OUTCOMES_TOUR_CHAPTER_ORDER, OutcomesTourChapter, OutcomesTourKind } from "consts";

import { FarmFeatureFlagsResponse } from "models";

import { FieldAdmin } from "pages/admin/verifications/QuickFieldAccess";
import { FieldFilterOptionsData } from "pages/app/all-field-view/components/FieldFilterOptions";
import { FieldSortOptionData } from "pages/app/all-field-view/components/FieldSortOptions";
import { AllFieldsDataTableFiltersData } from "pages/app/all-fields-data-table-view/components/AllFieldsDataTableFilters";

export const setLocalKlimVar = (item: string, value: unknown) => {
  const klimSession = JSON.parse(localStorage.getItem("klimSession") || "{}");
  const updated = { ...klimSession };
  updated[item] = value;
  return localStorage.setItem("klimSession", JSON.stringify(updated));
};

export const getLocalKlimVar = (item: string) => {
  const klimSession = JSON.parse(localStorage.getItem("klimSession") || "{}");
  if (localStorage.getItem(item)) {
    const value = localStorage.getItem(item);
    const updated = { ...klimSession };
    updated[item] = value;
    localStorage.setItem("klimSession", JSON.stringify(updated));
    localStorage.removeItem(item);
  }

  if (klimSession && Object.prototype.hasOwnProperty.call(klimSession, item)) {
    return klimSession[item];
  }
  return null;
};

export const clearLocalKlimVar = (item: string) => {
  const klimSession = JSON.parse(localStorage.getItem("klimSession") || "{}");
  const updated = { ...klimSession };
  if (updated[item]) {
    delete updated[item];
  }
  return localStorage.setItem("klimSession", JSON.stringify(updated));
};

export const getLocalVar = (item: string) => localStorage.getItem(item);

const ATTRIBUTION_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "partner", "referrer"] as const;

export const captureQueryParams = (search: string): void => {
  const params = new URLSearchParams(search);
  ATTRIBUTION_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value) setLocalKlimVar(key, value);
  });
};

export const clearAttributionParams = (): void => {
  ATTRIBUTION_PARAMS.forEach((key) => clearLocalKlimVar(key));
};

export const getLocalStorageShowFieldRecommendations = (): boolean | null => getLocalKlimVar("showFieldRecommendations");
export const setLocalStorageShowFieldRecommendations = (value: boolean) => setLocalKlimVar("showFieldRecommendations", value);

export const getLocalStorageImpactPanelCollapsed = (): boolean => !!getLocalKlimVar("impactPanelCollapsed");
export const setLocalStorageImpactPanelCollapsed = (value: boolean) => setLocalKlimVar("impactPanelCollapsed", value);

export const getAdminPreviousOpenFields = (): FieldAdmin[] | null => getLocalKlimVar("previousFields");
export const setAdminPreviousOpenFields = (value: FieldAdmin[]) => setLocalKlimVar("previousFields", value);

export const getLocalStorageFieldFilterOptions = (): FieldFilterOptionsData | null => {
  const farmFilterData = getLocalKlimVar("fieldFilterOptions");
  return farmFilterData || null;
};
export const setLocalStorageFieldFilterOptions = (data: FieldFilterOptionsData) => {
  setLocalKlimVar("fieldFilterOptions", data);
};
export const clearLocalStorageFieldFilterOptions = () => {
  clearLocalKlimVar("fieldFilterOptions");
};

export const getLocalStorageFieldSortOption = (farmId: string): FieldSortOptionData["sortOption"] | null => {
  const data: FieldSortOptionData | null = getLocalKlimVar("fieldSortOption");
  return data?.farmId === farmId ? data.sortOption ?? null : null;
};
export const setLocalStorageFieldSortOption = (data: FieldSortOptionData) => {
  setLocalKlimVar("fieldSortOption", data);
};
export const clearLocalStorageFieldSortOption = () => {
  clearLocalKlimVar("fieldSortOption");
};

export const getLocalStorageFieldSearchQuery = (farmId: string): string => {
  const data: { farmId: string; query: string } | null = getLocalKlimVar("fieldSearchQuery");
  return data?.farmId === farmId ? data.query ?? "" : "";
};
export const setLocalStorageFieldSearchQuery = (farmId: string, query: string) => {
  setLocalKlimVar("fieldSearchQuery", { farmId, query });
};
export const clearLocalStorageFieldSearchQuery = () => {
  clearLocalKlimVar("fieldSearchQuery");
};

export const getLocalStorageLastViewedYear = (farmId: string): number | undefined => {
  const data: { farmId: string; year: number } | null = getLocalKlimVar("lastViewedYear");
  return data?.farmId === farmId ? data.year : undefined;
};
export const setLocalStorageLastViewedYear = (farmId: string, year: number) => {
  setLocalKlimVar("lastViewedYear", { farmId, year });
};

export const getLocalStorageShowBaselineYears = (farmId: string): boolean => {
  const data: { farmId: string; showBaselineYears: boolean } | null = getLocalKlimVar("showBaselineYears");
  return data?.farmId === farmId ? data.showBaselineYears : false;
};
export const setLocalStorageShowBaselineYears = (farmId: string, showBaselineYears: boolean) => {
  setLocalKlimVar("showBaselineYears", { farmId, showBaselineYears });
};

export const getLocalStorageShowCropDetails = (farmId: string): boolean => {
  const data: { farmId: string; showCropDetails: boolean } | null = getLocalKlimVar("showCropDetails");
  return data?.farmId === farmId ? data.showCropDetails : false;
};
export const setLocalStorageShowCropDetails = (farmId: string, showCropDetails: boolean) => {
  setLocalKlimVar("showCropDetails", { farmId, showCropDetails });
};

// /all-fields/table-view specific filters: the missing-type checkboxes and the main/cover/previous crop filter
// semantics carried over from the old /historical-data table. Harvest year and search/sort/data-status filters
// live in the shared fieldFilterOptions/fieldSortOption/fieldSearchQuery keys above so they carry over from Timeline.
export const getLocalStorageAllFieldsDataTableFilters = (farmId: string): AllFieldsDataTableFiltersData | null => {
  const data: AllFieldsDataTableFiltersData | null = getLocalKlimVar("allFieldsDataTableFilters");
  return data?.farmId === farmId ? data : null;
};
export const setLocalStorageAllFieldsDataTableFilters = (data: AllFieldsDataTableFiltersData) => {
  setLocalKlimVar("allFieldsDataTableFilters", data);
};
export const clearLocalStorageAllFieldsDataTableFilters = () => {
  clearLocalKlimVar("allFieldsDataTableFilters");
};

// "Do not show again" on the bulk drawer's overwrite warning — farm-agnostic on purpose, it explains how bulk saving
// works rather than anything about a specific farm's data
export const getLocalStorageBulkEditWarningDismissed = (): boolean => !!getLocalKlimVar("bulkEditWarningDismissed");
export const setLocalStorageBulkEditWarningDismissed = (value: boolean) => setLocalKlimVar("bulkEditWarningDismissed", value);

// "Do not ask me again" on the bulk drawer's overwrite confirmation, farm-agnostic for the same reason as the banner
export const getLocalStorageBulkReplaceConfirmationDismissed = (): boolean => !!getLocalKlimVar("bulkReplaceConfirmationDismissed");
export const setLocalStorageBulkReplaceConfirmationDismissed = (value: boolean) => setLocalKlimVar("bulkReplaceConfirmationDismissed", value);

export const getLocalStorageFarmFeatureFlags = (): FarmFeatureFlagsResponse[] => getLocalKlimVar("farmFeatureFlags") ?? [];

export const setLocalStorageFarmFeatureFlags = (farmFeatureFlags: FarmFeatureFlagsResponse[]) => setLocalKlimVar("farmFeatureFlags", farmFeatureFlags);

/** Current Soil Sight project id — a single bare id, not scoped per Klim farm. The scenario-analysis
 * page reconciles it against the current farm's project list and overwrites it when it is foreign. */
const CURRENT_SOIL_SIGHT_PROJECT_ID_KEY = "currentSoilSightProjectId";

// Legacy per-farm map ({ [klimFarmId]: projectId }) — never read or written anymore, dropped on sign-out.
const LEGACY_SOIL_SIGHT_PROJECT_IDS_KEY = "soilSightsFarmIdsByKlimFarm";

export const getStoredSoilSightProjectId = (): string | null => {
  const value = getLocalKlimVar(CURRENT_SOIL_SIGHT_PROJECT_ID_KEY);
  // non-string guards against a legacy map object stored under the new key
  return typeof value === "string" && value ? value : null;
};

export const setStoredSoilSightProjectId = (projectId: string): void => {
  if (!projectId) return;
  setLocalKlimVar(CURRENT_SOIL_SIGHT_PROJECT_ID_KEY, projectId);
};

/** Call on sign-out and when resetting to the no-project state. */
export const clearStoredSoilSightProjectId = (): void => {
  clearLocalKlimVar(CURRENT_SOIL_SIGHT_PROJECT_ID_KEY);
  clearLocalKlimVar(LEGACY_SOIL_SIGHT_PROJECT_IDS_KEY);
};

// ─── SoilSight outcomes tour ──────────────────────────────────────────────────

interface OutcomesTourProgress {
  dismissed: boolean;
  seenChapters: OutcomesTourChapter[];
}

// Per device, not per farm or user: a new project never restarts the tour, and sign-out leaves it alone.
const OUTCOMES_TOUR_KEY = "soilSightOutcomesTour";

const NO_OUTCOMES_TOUR_PROGRESS: OutcomesTourProgress = { dismissed: false, seenChapters: [] };

export const getOutcomesTourProgress = (kind: OutcomesTourKind): OutcomesTourProgress => {
  try {
    const stored = getLocalKlimVar(OUTCOMES_TOUR_KEY)?.[kind];
    const seenChapters = Array.isArray(stored?.seenChapters) ? stored.seenChapters : [];
    // A renamed or removed chapter is dropped rather than carried forward.
    return { dismissed: Boolean(stored?.dismissed), seenChapters: OUTCOMES_TOUR_CHAPTER_ORDER.filter((chapter) => seenChapters.includes(chapter)) };
  } catch {
    // Unreadable storage (blocked, or corrupt JSON) reads as a first visit rather than breaking the page.
    return NO_OUTCOMES_TOUR_PROGRESS;
  }
};

/** Best effort: if storage cannot be written, the tour still closes and simply is not remembered. */
export const updateOutcomesTourProgress = (kind: OutcomesTourKind, update: (progress: OutcomesTourProgress) => OutcomesTourProgress): void => {
  try {
    const existing = getLocalKlimVar(OUTCOMES_TOUR_KEY) ?? {};
    setLocalKlimVar(OUTCOMES_TOUR_KEY, { ...existing, [kind]: update(getOutcomesTourProgress(kind)) });
  } catch {
    // Nothing to do: progress is a convenience, not data the farmer would lose.
  }
};

/** The home page caches a farm's geocoded location under this prefix plus the farm id (see useFarmLocation). */
export const FARM_LOCATION_CACHE_PREFIX = "home_farm_location_";

/** Drops every cached farm location: called wherever the persisted query cache is cleared, so the next user never sees the previous user's farms. */
export const clearStoredFarmLocations = () => {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(FARM_LOCATION_CACHE_PREFIX))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    // storage blocked: nothing was cached either
  }
};
